import React from "react"
import type { DevToolsProgramSnapshot, DevToolsUserSnapshot } from "@internal/dev-tools"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { useMemberProgram, useMemberProgramFlags } from "#/services/user/hooks/useMemberProgram"
import { getAppEnv } from "#/utils/env"

/**
 * Maps the host's user context onto the shape the dev-tools package renders.
 *
 * The mapping lives here rather than in the package for two reasons: the package must not
 * depend on host types, and picking fields explicitly keeps the PII that reaches the page
 * reviewable in one place. Never spread the whole context — it carries the refresh/update
 * callbacks plus the full partner and insurance blocks.
 *
 * Safe to call unconditionally: it runs in production too (where the route that consumes it
 * is never registered) and just produces an object nothing reads.
 */
export function useDevToolsUserSnapshot(): DevToolsUserSnapshot {
  const currentUser = useCurrentUser()
  // Both hooks share the "GetMemberProgram" query key, so this is one request, not two — and
  // MainNavigation already issues it on every authenticated page, so the dev tools add none.
  // Reading the flags through useMemberProgramFlags keeps the isConsumer/isEngage derivation in
  // its one canonical place instead of re-comparing programType strings here.
  const programQuery = useMemberProgram()
  const programFlagsQuery = useMemberProgramFlags()

  const programStatus: DevToolsProgramSnapshot["status"] = programQuery.isLoading
    ? "loading"
    : programQuery.isError
    ? "error"
    : "ready"

  return React.useMemo(
    () => ({
      appEnv: getAppEnv(),
      appVersion: __APP_VERSION__ || "local",

      carrotUserId: currentUser?.carrotUserId ?? null,
      employeeId: currentUser?.employeeId ?? null,
      memberId: currentUser?.memberId ?? null,

      email: currentUser?.email ?? null,
      preferredEmail: currentUser?.preferredEmail ?? null,
      locale: currentUser?.locale ?? null,

      isTestUser: currentUser?.isTestUser ?? null,
      isPosingAsMember: currentUser?.isPosingAsMember ?? null,
      isInAppLockdown: currentUser?.isInAppLockdown ?? null,

      carrotPlansRoutingCompleted: currentUser?.carrotPlansRoutingCompleted ?? null,
      benefitEnrollmentWizardCompleted: currentUser?.benefitEnrollmentWizardCompleted ?? null,

      company: currentUser?.company
        ? {
            companyId: currentUser.company.companyId ?? null,
            companyName: currentUser.company.companyName ?? null,
            parentCompanyId: currentUser.company.parentCompanyId ?? null,
            countryCode: currentUser.company.countryCode ?? null
          }
        : null,

      program: {
        status: programStatus,
        programType: programQuery.data?.programType ?? null,
        isCarrotLite: programQuery.data?.isCarrotLite ?? null,
        isConsumer: programFlagsQuery.data?.isConsumer ?? null,
        isEngage: programFlagsQuery.data?.isEngage ?? null
      }
    }),
    [currentUser, programStatus, programQuery.data, programFlagsQuery.data]
  )
}
